import React, { useState, useEffect } from 'react';
import { StyleSheet, View, Text, ScrollView, Image, TouchableOpacity, Alert, Share, ActivityIndicator, Switch, Modal, TextInput } from 'react-native';
import { NativeStackScreenProps } from '@react-navigation/native-stack';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { signOut, deleteUser } from 'firebase/auth';
import { auth, firestore } from '../../services/firebase';
import { MainTabParamList, MainStackParamList } from '../../navigation/MainNavigator';
import { User } from '../../types';
import { getBMICategory } from '../../utils/validation';
import { colors, spacing, typography, borderRadius } from '../../constants/theme';
import { Ionicons } from '@expo/vector-icons';
import Card from '../../components/Card';
import Button from '../../components/Button';
import ThemeToggle from '../../components/ThemeToggle';
import { useTheme } from '../../context/ThemeContext';
import Badge from '../../components/Badge';
import { appConfig } from '../../config/appConfig';
import { useNotificationsListener } from '../../hooks/useNotificationsListener';

type Props = NativeStackScreenProps<MainTabParamList & MainStackParamList, 'Profile'>;

const ProfileScreen = ({ navigation }: Props) => {
  const [userData, setUserData] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const { theme, isDarkMode } = useTheme();
  const colors = theme.colors;
  const [error, setError] = useState<string | null>(null);
  const [shareHeight, setShareHeight] = useState(false);
  const [shareWeight, setShareWeight] = useState(false);
  const [shareProgress, setShareProgress] = useState(false);
  const { friendRequestsCount } = useNotificationsListener();
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deletingAccount, setDeletingAccount] = useState(false);

  useEffect(() => {
    fetchUserData();
  }, []);

  const fetchUserData = async () => {
    setLoading(true);
    setError(null);
    try {
      const userId = auth.currentUser?.uid;
      
      if (!userId) {
        throw new Error('Usuario no autenticado');
      }
      
      const userRef = doc(firestore, 'users', userId);
      const userDoc = await getDoc(userRef);
      
      if (!userDoc.exists()) {
        throw new Error('No se encontró el perfil de usuario');
      }
      
      const userData = userDoc.data() as User;
      setUserData(userData);
      setShareHeight(userData.shareHeight || false);
      setShareWeight(userData.shareWeight || false);
      setShareProgress(userData.shareProgress || false);
    } catch (error) {
      console.error('Error al obtener datos del usuario:', error);
      setError(error instanceof Error ? error.message : 'Error al obtener datos del usuario');
    } finally {
      setLoading(false);
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut(auth);
      // No need to navigate, the auth state listener will handle it
    } catch (error) {
      console.error('Error al cerrar sesión:', error);
      Alert.alert('Error', 'No se pudo cerrar sesión');
    }
  };

  const shareFriendCode = async () => {
    if (!userData?.friendCode) return;
    
    try {
      await Share.share({
        message: `¡Únete a mí en GymBRO! Usa mi código de amigo: ${userData.friendCode}`,
      });
    } catch (error) {
      console.error('Error al compartir código:', error);
    }
  };

  const updatePrivacySettings = async () => {
    try {
      setLoading(true);
      const userId = auth.currentUser?.uid;
      if (!userId || !userData) {
        throw new Error('No se encontró un usuario autenticado');
      }

      await updateDoc(doc(firestore, 'users', userId), {
        shareHeight,
        shareWeight,
        shareProgress,
      });

      Alert.alert('Éxito', 'Configuración de privacidad actualizada correctamente');
    } catch (err: any) {
      console.error('Error al actualizar la configuración de privacidad:', err);
      Alert.alert('Error', 'No se pudo actualizar la configuración de privacidad');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAccount = () => {
    setDeleteModalVisible(true);
  };

  const confirmDeleteAccount = async () => {
    if (deleteConfirmText !== 'ELIMINAR') {
      Alert.alert('Error', 'Por favor escribe ELIMINAR para confirmar');
      return;
    }

    try {
      setDeletingAccount(true);
      const user = auth.currentUser;
      
      if (!user) {
        throw new Error('Usuario no autenticado');
      }
      
      // 1. Marcar como eliminado en Firestore (borrado lógico)
      const userRef = doc(firestore, 'users', user.uid);
      await updateDoc(userRef, {
        isDeleted: true,
        deletedAt: new Date(),
      });
      
      // 2. Eliminar cuenta de Firebase Auth
      await deleteUser(user);
      
      // Cerrar modal y sesión
      setDeleteModalVisible(false);
      
      // No es necesario navegar, el listener de autenticación lo manejará
    } catch (error) {
      console.error('Error al eliminar cuenta:', error);
      setDeletingAccount(false);
      Alert.alert('Error', 'No se pudo eliminar la cuenta. Es posible que necesites iniciar sesión nuevamente para completar esta acción.');
    }
  };

  if (loading) {
    return (
      <View style={[styles.loadingContainer, { backgroundColor: colors.background }]}>
        <ActivityIndicator size="large" color={colors.primary} />
      </View>
    );
  }

  if (error) {
    return (
      <View style={[styles.errorContainer, { backgroundColor: colors.background }]}>
        <Ionicons name="alert-circle-outline" size={64} color={colors.error} />
        <Text style={[styles.errorText, { color: colors.error }]}>{error}</Text>
        <View style={styles.errorButtonContainer}>
          <Button 
            title="Reintentar" 
            onPress={fetchUserData} 
            type="primary" 
            style={styles.errorButton} 
          />
          <Button 
            title="Cerrar Sesión" 
            onPress={handleSignOut} 
            type="outline" 
            style={styles.errorButton} 
          />
        </View>
      </View>
    );
  }

  if (!userData) {
    return (
      <View style={[styles.errorContainer, { backgroundColor: colors.background }]}>
        <Ionicons name="alert-circle-outline" size={64} color={colors.error} />
        <Text style={[styles.errorText, { color: colors.error }]}>No se pudo cargar el perfil</Text>
        <View style={styles.errorButtonContainer}>
          <Button 
            title="Reintentar" 
            onPress={fetchUserData} 
            type="primary" 
            style={styles.errorButton} 
          />
          <Button 
            title="Cerrar Sesión" 
            onPress={handleSignOut} 
            type="outline" 
            style={styles.errorButton} 
          />
        </View>
      </View>
    );
  }

  const getBmiStatusColor = (bmi: number | undefined) => {
    if (!bmi) return colors.grey.medium;
    if (bmi < 18.5) return colors.info;
    if (bmi < 25) return colors.success;
    if (bmi < 30) return colors.warning;
    return colors.error;
  };

  return (
    <ScrollView style={[styles.scrollView, { backgroundColor: colors.background }]}>
      <View style={styles.container}>
        {/* Profile Header */}
        <View style={styles.header}>
          <View style={styles.profileImageContainer}>
            {userData.profilePhoto ? (
              <Image 
                source={{ uri: userData.profilePhoto }} 
                style={styles.profileImage} 
              />
            ) : (
              <View style={[styles.profileImage, styles.profileImagePlaceholder]}>
                <Text style={styles.profileInitials}>
                  {userData.firstName?.charAt(0)}{userData.lastName?.charAt(0)}
                </Text>
              </View>
            )}
          </View>
          
          <Text style={[styles.username, { color: colors.grey.dark }]}>@{userData.username}</Text>
          <Text style={[styles.name, { color: colors.text }]}>{userData.firstName} {userData.lastName}</Text>
        </View>

        {/* Body Measurements Card */}
        <Card style={styles.card}>
          <View style={styles.cardHeader}>
            <Text style={[styles.cardTitle, { color: colors.text }]}>Medidas Corporales</Text>
            <TouchableOpacity
              onPress={() => navigation.navigate('ProfileSetup')}
            >
              <Ionicons name="pencil" size={20} color={colors.primary} />
            </TouchableOpacity>
          </View>
          
          <View style={styles.measurementsContainer}>
            <View style={styles.measurementItem}>
              <Text style={[styles.measurementLabel, { color: colors.grey.dark }]}>Altura</Text>
              <Text style={[styles.measurementValue, { color: colors.text }]}>
                {userData.height ? `${userData.height} cm` : 'No especificado'}
              </Text>
            </View>
            
            <View style={styles.measurementItem}>
              <Text style={[styles.measurementLabel, { color: colors.grey.dark }]}>Peso</Text>
              <Text style={[styles.measurementValue, { color: colors.text }]}>
                {userData.weight ? `${userData.weight} kg` : 'No especificado'}
              </Text>
            </View>
            
            {userData.bmi && (
              <View style={styles.measurementItem}>
                <Text style={[styles.measurementLabel, { color: colors.grey.dark }]}>IMC</Text>
                <Text style={[styles.measurementValue, { color: getBmiStatusColor(userData.bmi) }]}>
                  {userData.bmi.toFixed(1)} - {getBMICategory(userData.bmi)}
                </Text>
              </View>
            )}
            
            {userData.bodyFatPercentage && (
              <View style={styles.measurementItem}>
                <Text style={[styles.measurementLabel, { color: colors.grey.dark }]}>% Grasa Corporal</Text>
                <Text style={[styles.measurementValue, { color: colors.text }]}>{userData.bodyFatPercentage}%</Text>
              </View>
            )}
            
            {userData.visceralFat && (
              <View style={styles.measurementItem}>
                <Text style={[styles.measurementLabel, { color: colors.grey.dark }]}>Grasa Visceral</Text>
                <Text style={[styles.measurementValue, { color: colors.text }]}>{userData.visceralFat}</Text>
              </View>
            )}
          </View>
        </Card>

        {/* Privacy Settings Card */}
        <Card style={styles.card}>
          <View style={styles.cardHeader}>
            <Text style={[styles.cardTitle, { color: colors.text }]}>Configuración de Privacidad</Text>
          </View>
          <View style={styles.settingsContainer}>
            <View style={styles.settingItem}>
              <Text style={[styles.settingLabel, { color: colors.text }]}>Compartir altura</Text>
              <Switch
                value={shareHeight}
                onValueChange={setShareHeight}
                trackColor={{ false: colors.grey.medium, true: colors.primary }}
                thumbColor={colors.white}
              />
            </View>
            <View style={styles.settingItem}>
              <Text style={[styles.settingLabel, { color: colors.text }]}>Compartir peso</Text>
              <Switch
                value={shareWeight}
                onValueChange={setShareWeight}
                trackColor={{ false: colors.grey.medium, true: colors.primary }}
                thumbColor={colors.white}
              />
            </View>
            <View style={styles.settingItem}>
              <Text style={[styles.settingLabel, { color: colors.text }]}>Compartir progreso</Text>
              <Switch
                value={shareProgress}
                onValueChange={setShareProgress}
                trackColor={{ false: colors.grey.medium, true: colors.primary }}
                thumbColor={colors.white}
              />
            </View>
            <Button 
              title="Guardar cambios" 
              onPress={updatePrivacySettings} 
              style={styles.saveButton}
              type="primary"
            />
          </View>
        </Card>

        {/* Friend Code Card */}
        <Card style={styles.card}>
          <View style={styles.cardHeader}>
            <Text style={[styles.cardTitle, { color: colors.text }]}>Código de Amigo</Text>
            <TouchableOpacity onPress={shareFriendCode}>
              <Ionicons name="share-social-outline" size={20} color={colors.primary} />
            </TouchableOpacity>
          </View>
          
          <View style={styles.friendCodeContainer}>
            <Text style={[styles.friendCode, { color: colors.primary }]}>{userData.friendCode}</Text>
            <Text style={[styles.friendCodeHelp, { color: colors.grey.dark }]}>
              Comparte este código con tus amigos para que te agreguen
            </Text>
          </View>
        </Card>

        {/* Apariencia Card */}
        <Card style={styles.card}>
          <View style={styles.cardHeader}>
            <Text style={[styles.cardTitle, { color: colors.text }]}>Apariencia</Text>
          </View>
          
          <View style={styles.themeContainer}>
            <ThemeToggle />
          </View>
        </Card>

        {/* Actions */}
        <View style={styles.actionContainer}>
          <View style={styles.buttonWrapper}>
            <Button
              title="Amigos"
              onPress={() => navigation.navigate('FriendManagement')}
              type="outline"
              style={styles.actionButton}
            />
            {friendRequestsCount > 0 && (
              <Badge count={friendRequestsCount} color={theme.colors.error} />
            )}
          </View>
          
          <Button
            title="Historial de Progreso"
            onPress={() => navigation.navigate('Progress')}
            type="outline"
            style={{ ...styles.actionButton, marginTop: spacing.md }}
          />
          
          <Button
            title="Cerrar Sesión"
            onPress={handleSignOut}
            type="outline"
            style={{ ...styles.signOutButton, marginTop: spacing.md }}
          />

          <View style={[styles.separator, { backgroundColor: isDarkMode ? colors.white : colors.grey.medium }]} />
          
          <Button
            title="Eliminar Cuenta"
            onPress={handleDeleteAccount}
            type="outline"
            style={styles.deleteButton}
          />

          {/* Versión de la app y enlaces legales */}
          <View style={styles.appInfoContainer}>
            <Text style={[styles.versionText, { color: colors.grey.medium }]}>
              {appConfig.name} v{appConfig.version}
            </Text>
            <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: spacing.xs }}>
              <TouchableOpacity onPress={() => navigation.navigate('TermsConditions')}>
                <Text style={[styles.termsText, { color: colors.primary }]}>
                  Términos y Condiciones
                </Text>
              </TouchableOpacity>
              <Text style={{ fontSize: typography.fontSize.sm, marginHorizontal: spacing.xs, color: colors.grey.medium }}>|</Text>
              <TouchableOpacity onPress={() => navigation.navigate('PrivacyPolicy')}>
                <Text style={[styles.termsText, { color: colors.primary }]}>
                  Política de Privacidad
                </Text>
              </TouchableOpacity>
            </View>
            <Text style={[styles.copyrightText, { color: colors.grey.medium }]}>
              {appConfig.copyrightText}
            </Text>
          </View>
        </View>

        {/* Modal de confirmación para eliminar cuenta */}
        <Modal
          visible={deleteModalVisible}
          transparent={true}
          animationType="fade"
          onRequestClose={() => setDeleteModalVisible(false)}
        >
          <View style={styles.modalOverlay}>
            <View style={[styles.modalContainer, { backgroundColor: colors.card }]}>
              <Text style={[styles.modalTitle, { color: colors.error }]}>Eliminar Cuenta</Text>
              
              <Text style={[styles.modalText, { color: colors.text }]}>
                Esta acción eliminará permanentemente tu cuenta y todos tus datos. No podrás recuperarlos después.
              </Text>
              
              <Text style={[styles.confirmInstructions, { color: colors.text }]}>
                Escribe ELIMINAR para confirmar:
              </Text>
              
              <TouchableOpacity 
                style={[styles.confirmInput, { borderColor: colors.grey.light }]}
                onPress={() => Alert.alert('Confirmación', 'Escribe ELIMINAR para confirmar la eliminación de tu cuenta')}
              >
                <TextInput
                  style={[styles.confirmInputText, { color: colors.text }]}
                  placeholder="ELIMINAR"
                  placeholderTextColor={colors.grey.medium}
                  value={deleteConfirmText}
                  onChangeText={setDeleteConfirmText}
                  autoCapitalize="characters"
                />
              </TouchableOpacity>
              
              <View style={styles.modalButtons}>
                <Button
                  title="Cancelar"
                  onPress={() => setDeleteModalVisible(false)}
                  type="outline"
                  style={styles.modalButton}
                />
                
                <Button
                  title="Eliminar Cuenta"
                  onPress={confirmDeleteAccount}
                  type="primary"
                  style={{ 
                    ...styles.modalButton, 
                    backgroundColor: colors.error 
                  }}
                  textStyle={{ color: colors.white }}
                  loading={deletingAccount}
                  disabled={deleteConfirmText !== 'ELIMINAR' || deletingAccount}
                />
              </View>
            </View>
          </View>
        </Modal>
      </View>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  scrollView: {
    flex: 1,
  },
  container: {
    flex: 1,
    padding: spacing.lg,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: spacing.lg,
  },
  errorText: {
    fontSize: typography.fontSize.lg,
    marginVertical: spacing.md,
    textAlign: 'center',
  },
  errorButtonContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    width: '100%',
    marginTop: spacing.md,
    gap: spacing.md,
  },
  errorButton: {
    flex: 1,
    maxWidth: 180,
  },
  header: {
    alignItems: 'center',
    marginBottom: spacing.xl,
  },
  profileImageContainer: {
    marginBottom: spacing.md,
  },
  profileImage: {
    width: 100,
    height: 100,
    borderRadius: 50,
  },
  profileImagePlaceholder: {
    backgroundColor: colors.primary,
    justifyContent: 'center',
    alignItems: 'center',
  },
  profileInitials: {
    color: colors.white,
    fontSize: typography.fontSize.xl,
    fontWeight: 'bold',
  },
  username: {
    fontSize: typography.fontSize.md,
    marginBottom: spacing.xs,
  },
  name: {
    fontSize: typography.fontSize.xl,
    fontWeight: 'bold',
  },
  card: {
    marginBottom: spacing.lg,
  },
  cardHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: spacing.md,
  },
  cardTitle: {
    fontSize: typography.fontSize.lg,
    fontWeight: 'bold',
  },
  measurementsContainer: {
    gap: spacing.md,
  },
  measurementItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  measurementLabel: {
    fontSize: typography.fontSize.md,
  },
  measurementValue: {
    fontSize: typography.fontSize.md,
    fontWeight: '500',
  },
  friendCodeContainer: {
    alignItems: 'center',
  },
  friendCode: {
    fontSize: typography.fontSize.xxl,
    fontWeight: 'bold',
    letterSpacing: 2,
    marginBottom: spacing.sm,
  },
  friendCodeHelp: {
    fontSize: typography.fontSize.sm,
    textAlign: 'center',
  },
  actionContainer: {
    marginTop: spacing.lg,
    paddingHorizontal: spacing.md,
  },
  actionButton: {
    width: '100%',
  },
  themeContainer: {
    paddingVertical: spacing.sm,
  },
  signOutButton: {
    width: '100%',
  },
  settingsContainer: {
    gap: spacing.md,
  },
  settingItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: spacing.xs,
  },
  settingLabel: {
    fontSize: typography.fontSize.md,
  },
  saveButton: {
    marginTop: spacing.md,
  },
  buttonWrapper: {
    position: 'relative',
    width: '100%',
  },
  deleteButton: {
    width: '100%',
    borderColor: colors.error,
    borderWidth: 1,
    marginTop: spacing.md,
  },
  appInfoContainer: {
    marginTop: spacing.xl,
    alignItems: 'center',
    justifyContent: 'center',
  },
  versionText: {
    fontSize: typography.fontSize.sm,
    marginBottom: spacing.xs,
  },
  termsText: {
    fontSize: typography.fontSize.sm,
    textDecorationLine: 'underline',
  },
  copyrightText: {
    fontSize: typography.fontSize.xs,
  },
  separator: {
    height: 1,
    width: '100%',
    marginTop: spacing.lg,
    marginBottom: spacing.md,
    opacity: 0.5,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: spacing.lg,
  },
  modalContainer: {
    width: '90%',
    borderRadius: borderRadius.lg,
    padding: spacing.lg,
    alignItems: 'center',
  },
  modalTitle: {
    fontSize: typography.fontSize.xl,
    fontWeight: 'bold',
    marginBottom: spacing.md,
  },
  modalText: {
    fontSize: typography.fontSize.md,
    textAlign: 'center',
    marginBottom: spacing.lg,
  },
  confirmInstructions: {
    fontSize: typography.fontSize.md,
    marginBottom: spacing.sm,
    alignSelf: 'flex-start',
  },
  confirmInput: {
    width: '100%',
    borderWidth: 1,
    borderRadius: borderRadius.md,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    marginBottom: spacing.lg,
  },
  confirmInputText: {
    fontSize: typography.fontSize.lg,
    fontWeight: 'bold',
    textAlign: 'center',
  },
  modalButtons: {
    flexDirection: 'row',
    width: '100%',
    justifyContent: 'space-between',
  },
  modalButton: {
    flex: 1,
    marginHorizontal: spacing.xs,
  },
  deleteConfirmButton: {
    backgroundColor: colors.error,
    borderColor: colors.error,
  },
});

export default ProfileScreen; 